import Amplify, {API, Auth, graphqlOperation} from 'aws-amplify';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import * as subscriptions from '../graphql/subscriptions';

import uuid from 'uuid/v4'
import * as request from 'request-promise'
import logMessage from "./logger";
import * as moment from 'moment';

let EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/igm;

export class KaydohChat {

    private readonly url;
    private readonly clientId;
    private currentUser;
    private conversation;
    private readonly conversationConfigKey;
    private readonly membersConfigKey;
    private readonly userConfigKey;
    private messageListeners = [];
    private isLoggedIn = false;
    private isReady = false;
    private hasInitializedChat = false;


    /*UI variables*/
    private parentContainer: HTMLElement;
    private readonly config: ChatConfig;
    private chatConfigStore: ChatConfigStore;

    constructor(url, clientId: String, config: ChatConfig, container: HTMLElement) {
        this.url = url;
        this.clientId = clientId;
        this.config = config;
        this.parentContainer = container;

        this.conversationConfigKey = "conversationId";
        this.membersConfigKey = "members";
        this.userConfigKey = "currentUser";

        this.init()
            .then(() => {
                if (this.config.autoPopUp) {
                    setTimeout(() => {
                        this.onChatIconClicked(null);
                    }, this.config.autoPopUpDelay)
                }
            });
    };

    init = async () => {
        this.chatConfigStore = new ChatConfigStore(this.clientId);
        try {
            await this.initChatConfig();
            this.initUI();

            let userConfig = this.chatConfigStore.retrieve(this.userConfigKey);
            if (userConfig != null) {
                this.currentUser = {
                    user: JSON.parse(userConfig)
                };
                this.currentUser.chatql = await Auth.currentAuthenticatedUser();
                if (this.currentUser.chatql) {
                    this.isLoggedIn = true;
                    await this.initConversation()
                }
            }
        } catch (e) {
            logMessage(e);
            throw e;
        }
    };

    isConversationCreated = (): boolean => {
        return this.conversation != null;
    };

    login = async (callback: (this, response: any) => void) => {
        function makeId(length, characters) {
            let result = '';
            let charactersLength = characters.length;
            for (let i = 0; i < length; i++) {
                result += characters.charAt(Math.floor(Math.random() * charactersLength));
            }
            return result;
        }

        let param: any = {
            clientId: this.clientId,
            sourceUrl: this.config.landingPageUrl
        };
        if (this.currentUser == null) {
            param.newUser = true;
            param.email = makeId(20, 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') + "@kaydoh-chat.com";
            param.password = makeId(40, '~:;IJK6789LMNOPVWXYZ!@AGH#$%abBCDEFcde^&*-fghijklmn_+=oQRSTUpqrstuvwxyz012345');
        } else {
            param.newUser = false;
            param.email = this.currentUser.chatql.username;
        }

        var res = await request({
            method: 'POST',
            uri: this.url + '/api/chat/auth',
            body: param,
            json: true
        });
        if (res.status) {
            this.chatConfigStore.store(this.membersConfigKey, JSON.stringify(res.data.members));
            if (this.currentUser == null) {
                this.currentUser = {password: param.password};
                this.currentUser.user = {id: res.data.chatqlUserId};
                this.chatConfigStore.store(this.userConfigKey, JSON.stringify(this.currentUser.user));
                this.currentUser.chatql = await Auth.signIn(param.email, param.password);
            }
            callback({status: true});
        } else {
            callback({status: false});
        }
    };

    createConversation = async () => {
        logMessage("creating conversation");
        const members = JSON.parse(this.chatConfigStore.retrieve(this.membersConfigKey));
        const result = await API.graphql(graphqlOperation(mutations.createConversation, {
            input: {
                id: uuid(),
                conversationUserId: this.currentUser.user.id,
                sourceUrl: this.config.landingPageUrl,
                members: members,
                createdAt: new Date()
            }
        }));
        logMessage("created conversation");
        // @ts-ignore
        this.conversation = result.data.createConversation;
        this.chatConfigStore.store(this.conversationConfigKey, this.conversation.id);
        return this.conversation;
    };

    createMessage = async (message: string) => {
        const chat = this;

        async function doCreateMessage(msg) {
            return API.graphql(graphqlOperation(mutations.createMessage, {
                input: {
                    id: uuid(),
                    messageConversationId: chat.conversation.id,
                    content: msg,
                    authorId: chat.currentUser.chatql.username,
                    createdAt: new Date()
                }
            }));
        }

        if (this.chatConfigStore.retrieve("accepted-disclaimer") == null) {
            let disclaimerContainer = document.getElementById("chat-window")
                .getElementsByClassName("chat-disclaimer")
                .item(0) as HTMLElement;

            await doCreateMessage(disclaimerContainer.innerText);
            this.chatConfigStore.store("accepted-disclaimer", true);
            disclaimerContainer.remove();
        }
        const result = await doCreateMessage(message);
        // @ts-ignore
        return result.data.createMessage
    };

    onMessage = (messages) => {
        let msgList = Array.isArray(messages) ? messages : [messages];
        //logMessage("onMessages:" + JSON.stringify(msgList));
        let lastReadTimestamp = this.chatConfigStore.getLastReadMessageTimestamp();
        msgList.forEach(m => {
            this.chatConfigStore.messages.push(m);
            let lastMessageTimestamp = new Date(m.createdAt);
            if (lastReadTimestamp < lastMessageTimestamp.getTime()) {
                this.chatConfigStore.increaseUnreadCount(1);
            }
        });
        if (!this.chatConfigStore.isChatWindowOpen && msgList.length > 0) {
            let lastMessageTimestamp = new Date(msgList[msgList.length - 1].createdAt);
            if (lastReadTimestamp < lastMessageTimestamp.getTime()) {
                this.renderChatNotification();
            }
        }
        this.renderMessages(msgList);
        this.messageListeners.forEach(function (listener) {
            listener(msgList);
        })
    };

    registerMessageListener = (listener: (this) => void) => {
        this.messageListeners.push(listener);
    };

    open = (event) => {
        if (this.chatConfigStore.isChatWindowOpen == false) {
            this.onChatIconClicked(event);
        }
    }

    close = (event) => {
        if (this.chatConfigStore.isChatWindowOpen == true) {
            this.onChatIconClicked(event);
        }
    }

    private async subscribe() {
        if (this.conversation != null) {
            const subscription = await API.graphql(graphqlOperation(subscriptions.onCreateMessage, {
                messageConversationId: this.conversation.id
            }));
            // @ts-ignore
            await subscription.subscribe({
                next: (message) => this.onMessage(message.value.data.onCreateMessage)
            });
            this.renderChatReady();
            this.isReady = true;
        }
    }

    private async initChatConfig() {
        var res = await request({
            method: 'GET',
            uri: this.url + '/api/chat/client?clientId=' + this.clientId,
            json: true
        });
        if (res.status) {
            //Amplify.configure(res.data.chatConfig);
            Auth.configure(res.data.chatConfig);
            API.configure(res.data.chatConfig);
            this.config.profileImageUrl = res.data.chatAttributes.profile.imageUrl;
            this.config.profileFullName = res.data.chatAttributes.profile.name;
            this.config.autoPopUp = res.data.chatAttributes.autoPopUp;
            this.config.autoPopUpDelay = res.data.chatAttributes.autoPopUpDelay;
            this.config.emailSolicitation = res.data.chatAttributes.emailSolicitation;
            this.config.welcomeMessage = res.data.chatAttributes.greeting;
            const theme = new Theme();
            theme.bgColorCode = res.data.chatAttributes.primaryColor ? res.data.chatAttributes.primaryColor : "#23A733";
            theme.name = "green";
            this.config.theme = theme;
            //this.config.secondaryColor = res.data.chatAttributes.secondaryColor;
            this.config.defaultOptions = res.data.chatAttributes.defaultOptions;
            console.log(this.config);
        } else {
            throw new Error("can't load the chat config");
        }
    }

    private initChat = async () => {
        if (this.hasInitializedChat == false) {
            this.hasInitializedChat = true;
            return this.login(async response => {
                logMessage("login: " + response.status);
                this.isLoggedIn = true;
                this.initConversation();
            });
        }
    };

    private initConversation = async () => {
        let conversationId = this.chatConfigStore.retrieve(this.conversationConfigKey);
        if (conversationId == null) {
            await this.createConversation();
            conversationId = this.chatConfigStore.retrieve(this.conversationConfigKey);
        }
        if (conversationId != null) {
            const result = await API.graphql(graphqlOperation(queries.getConversation, {
                id: conversationId
            }));
            // @ts-ignore
            this.conversation = result.data.getConversation;
            if (this.conversation.messages) {
                this.onMessage(this.conversation.messages.items)
            }
            this.subscribe();

        } else {
            logMessage("some problem conversationId is not set");
        }
    };

    private isEmailCollected = () => {
        return this.currentUser != null && this.currentUser.user.email != null
    };

    /**
     *
     * UI Code
     */

    initUI = () => {
        //Writing CSS
        // const style = document.createElement("style")
        // style.innerText = `#chat-icon, #chat-window {z-index: 999999} 
        //       #chat-window .header, #chat-window .message-container .message.thisuser .content-container { background: ${this.config.theme.bgColorCode}; color: #FFFFFF;}
        //       #chat-window .email-collection  input, #chat-window .phone-collection input {border: 0.5px solid ${this.config.theme.bgColorCode};}
        //       #chat-window .quick-message-input button {background: ${this.config.theme.bgColorCode};}`
        // // this.parentContainer.appendChild(style);

        let icon = document.createElement("div");
        icon.id = "chat-icon";
        icon.innerHTML = "<div class='chat-icon-container'><div class='chat-notification'></div><div class='icon' status='minimized'>" + this.createIcon(IconType.OPEN) + "</div></div>";
        icon.addEventListener("click", ev => this.onChatIconClicked(ev));
        this.parentContainer.appendChild(icon);
        this.renderChatWindow();
    };

    private async onChatIconClicked(event: Event) {
        /*if (!this.isLoggedIn) {
            await this.initChat();
        }*/
        let icon = document.getElementById("chat-icon").getElementsByClassName("icon").item(0);
        if (icon.getAttribute("status") == "minimized") {
            icon.innerHTML = this.createIcon(IconType.CLOSE);
            icon.setAttribute("status", "maximized");
            document.getElementById("chat-window").style.display = "block";
            let messageContainer = document.getElementById("kchat-message-container");
            messageContainer.scrollTop = messageContainer.scrollHeight;
            this.chatConfigStore.isChatWindowOpen = true;
            this.checkAndMarkAllMessagesRead();
        } else {
            icon.innerHTML = this.createIcon(IconType.OPEN);
            icon.setAttribute("status", "minimized");
            document.getElementById("chat-window").style.display = "none";
            this.chatConfigStore.isChatWindowOpen = false;
        }
    };

    private async onMessageInputKeypress(event: KeyboardEvent) {

        async function postMessage(chat: KaydohChat, message) {
            //logMessage("posting message: " + message);
            if (chat.isReady) {

                if (!chat.isEmailCollected()) {
                    chat.renderEmailCollection(null).then();
                }
                if (!chat.isEmailCollected() && EMAIL_REGEX.test(message)) {
                    let emailCollection = document.getElementsByClassName("email-collection").item(0) as HTMLInputElement;
                    await chat.updateUserEmail(message, null, emailCollection, null);
                } else {
                    await chat.createMessage(message);
                }
            } else {
                setTimeout(() => {
                    postMessage(chat, message)
                }, 4000);
            }
        }

        if (event.code == "Enter") {
            if (this.isReady === false && this.isConversationCreated() == false) {
                this.initChat();
            }
            this.hideDefaultOptions();
            var chatWindow = document.getElementById("chat-window");
            let input = chatWindow.getElementsByClassName("message-input")
                .item(0)
                .getElementsByTagName("input")
                .item(0) as HTMLInputElement;
            let message = input.value;
            if (this.isReady) {
                postMessage.call(this, this, message);
                input.value = "";
            } else {
                this.renderInitializing();
                postMessage.call(this, this, message);
                input.value = "";
            }
        }
    }

    private async onEmailSubmit(event: Event, option) {
        if (event instanceof MouseEvent || (event instanceof KeyboardEvent && event.code == "Enter")) {
            const chatInstance = this;
            let elements = document.getElementsByClassName("email-collection");
            let newEmail: string;
            let name: string;
            let message: string;
            var emailCollection: HTMLElement;
            emailCollection = elements.item(0) as HTMLElement;

            let emailInput = (emailCollection.getElementsByClassName("email-input").item(0) as HTMLElement)
                .getElementsByTagName("input").item(0) as HTMLInputElement;
            newEmail = emailInput.value;

            let nameInput = (emailCollection.getElementsByClassName("name-input").item(0) as HTMLElement)
                .getElementsByTagName("input").item(0) as HTMLInputElement;
            name = nameInput.value;

            let messageInput = (emailCollection.getElementsByClassName("quick-message-email").item(0) as HTMLElement)
                .getElementsByTagName("input").item(0) as HTMLInputElement;
            message = messageInput.value;
            // Concat the message
            if (option.message) option.message = option.message.concat(" - " + message)

            if (newEmail && newEmail != '' && await this.validateEmail(newEmail)) {
                if (emailCollection.getElementsByClassName("sys-message").length == 0) {
                    let element = document.createElement("div");
                    element.className = "sys-message";
                    element.innerHTML = "<div>Updating email, please wait..</div>";
                    emailCollection.append(element);
                    let messageContainer = document.getElementById("kchat-message-container");
                    messageContainer.scrollTop = messageContainer.scrollHeight;
                }
                if (!this.isLoggedIn) {
                    setTimeout(function () {
                        chatInstance.onEmailSubmit(event, option);
                    }, 1000);
                } else {
                    if (EMAIL_REGEX.test(newEmail)) {
                        await this.updateUserEmail(newEmail, name, emailCollection, option);
                        if (option) {
                            await this.createMessage(option.message + "\n" + messageInput + "\n" + option.others + "\n" + newEmail);
                        }

                    }
                }
            } else {
                console.log("email valid failed");
                alert("Please provide valid email");
            }
        }
    }

    private async onPhoneSubmit(event: Event, option) {
        if (event instanceof MouseEvent || (event instanceof KeyboardEvent && event.code == "Enter")) {
            const chatInstance = this;
            const elements = document.getElementsByClassName("phone-collection");
            const phoneCollection: HTMLElement = elements.item(0) as HTMLElement;

            if (phoneCollection.getElementsByClassName("sys-message").length == 0) {
                let element = document.createElement("div");
                element.className = "sys-message";
                element.innerHTML = "<div>Updating phone, please wait..</div>";
                phoneCollection.append(element);
                let messageContainer = document.getElementById("kchat-message-container");
                messageContainer.scrollTop = messageContainer.scrollHeight;
            }

            if (!this.isLoggedIn) {
                setTimeout(function () {
                    chatInstance.onPhoneSubmit(event, option);
                }, 1000);
            } else {
                let nameInput = (phoneCollection.getElementsByClassName("name-input").item(0) as HTMLElement)
                    .getElementsByTagName("input").item(0) as HTMLInputElement;
                let name = nameInput.value;

                let messageInput = (phoneCollection.getElementsByClassName("quick-message-phone").item(0) as HTMLElement)
                    .getElementsByTagName("input").item(0) as HTMLInputElement;
                let message = messageInput.value;
                // Concat the message
                if (option.message) option.message = option.message.concat(" - " + message)

                let phoneInput = (phoneCollection.getElementsByClassName("phone-input").item(0) as HTMLElement)
                    .getElementsByTagName("input").item(0) as HTMLInputElement;
                let newPhone = phoneInput.value;

                if (newPhone && newPhone != '' && newPhone.match(/\d+/g) != null) {
                    newPhone = newPhone.match(/\d+/g).map(Number).join("");
                    if (newPhone.length > 10) {
                        newPhone = newPhone.substr(1);
                    }
                    if (newPhone.length == 10) {
                        console.log("updating phone...");
                        await this.updateUserPhone(newPhone, name, phoneCollection, option);
                        await this.createMessage(option.message + "\n" + option.others + "\n" + newPhone);
                    } else {
                        alert("Please provide valid phone");
                    }
                } else {
                    alert("Please provide valid phone");
                }
            }
        }
    }

    private async updateUserEmail(newEmail, name, emailCollection: HTMLElement, option) {
        console.log(this.config.landingPageUrl);
        let param = {
            clientId: this.clientId,
            landingPageUrl: this.config.landingPageUrl,
            landingPageCode: this.config.landingPageCode,
            name: name,
            email: this.currentUser.chatql.username,
            newEmail: newEmail,
            extraMsg: null
        };
        if (option) {
            param.extraMsg = option.message
        }
        console.log(param);
        var res = await request({
            method: 'POST',
            uri: this.url + '/api/chat/user',
            body: param,
            json: true
        });
        if (res.status) {
            emailCollection.remove();
            this.currentUser.user.email = newEmail;
            this.chatConfigStore.store(this.userConfigKey, JSON.stringify(this.currentUser.user));
        }
    }

    private async updateUserPhone(phone, name, phoneCollection: HTMLElement, option) {
        let param = {
            clientId: this.clientId,
            landingPageUrl: this.config.landingPageUrl,
            landingPageCode: this.config.landingPageCode,
            name: name,
            email: this.currentUser.chatql.username,
            phone: phone,
            extraMsg: option.message
        };
        var res = await request({
            method: 'POST',
            uri: this.url + '/api/chat/user/phone',
            body: param,
            json: true
        });
        if (res.status) {
            phoneCollection.remove();
        }
    }

    private renderChatWindow() {

        let chatWindow = document.createElement("div");
        chatWindow.style.display = "none";
        chatWindow.id = "chat-window";
        chatWindow.innerHTML = `<div class='header'></div>
                                <div id='kchat-message-container' class='message-container'>
                                    <div id='messages-thread' class='messages-thread-container'></div>
                                </div>
                                <div class='chat-disclaimer'></div>
                                <div class='message-input' style='display: none;'></div>`;
        this.parentContainer.appendChild(chatWindow);
        if (this.chatConfigStore.retrieve("accepted-disclaimer") == null) {
            let disclaimerContainer = chatWindow.getElementsByClassName("chat-disclaimer").item(0) as HTMLElement;
            disclaimerContainer.innerHTML = "<div class=\"disclaimer\">By starting my chat here, I consent to Kaydoh's <a href=\"https://kaydoh.com/terms-and-conditions/\" target=\"_blank\">terms </a> / <a href=\"https://kaydoh.com/terms-and-conditions/\" target=\"_blank\">conditions</a>, agree Kaydoh may receive / process my personal data and use that data to communicate by calling or texting me about Kaydoh products and services. <a href=\"#\" class='optout'>Opt out here</a></div>\n";
            let optoutAnchor = chatWindow.getElementsByClassName("optout").item(0);
            optoutAnchor.addEventListener("click", evt => {
                this.close(evt);
                if (eval("window.showDnc")) {
                    eval("showDnc()");
                } else {
                    this.showDnc()
                }
            });
        }

        this.renderHeader(chatWindow);
        let messageInput = chatWindow.getElementsByClassName("message-input").item(0);
        messageInput.innerHTML = "<input type='text' placeholder='Ask us anything..'>";

        let input = messageInput.getElementsByTagName("input").item(0) as HTMLInputElement;
        input.addEventListener("keypress", ev => this.onMessageInputKeypress(ev));

        let messageContainer = chatWindow.getElementsByClassName("message-container").item(0) as HTMLElement;
        messageContainer.style.maxHeight = window.innerHeight * 0.5 + "px";
        messageContainer.addEventListener("scroll", ev => {
            this.checkAndMarkAllMessagesRead()
        });
        this.renderDefaultOptions(messageContainer, input);
        window.addEventListener('resize', ev => {
            messageContainer.style.maxHeight = window.innerHeight * 0.5 + "px"
        });

    }

    private renderHeader(chatWindow) {
        let header = chatWindow.getElementsByClassName("header").item(0);
        header.className += (" " + this.config.theme.name);
        header.innerHTML = "<div class='profile-image'></div><div class='name-status'><div class='name'></div><div class='status-txt'></div></div><div class='welcome'></div>";
        let profileImage = header.getElementsByClassName("profile-image").item(0) as HTMLElement;
        profileImage.innerHTML = "<div class='image-container'><img><div class='chat-status'></div></div>";
        profileImage.getElementsByClassName("image-container")
            .item(0)
            .getElementsByTagName("img")
            .item(0)
            .src = this.config.profileImageUrl;
        header.getElementsByClassName("name").item(0).innerText = this.config.profileFullName;
        if (this.config.availableOnline) {
            (header.getElementsByClassName("chat-status").item(0) as HTMLElement).className += " online";
            (header.getElementsByClassName("status-txt").item(0) as HTMLElement).innerHTML = "active";
        } else {
            (header.getElementsByClassName("chat-status").item(0) as HTMLElement).className += " offline";
            (header.getElementsByClassName("status-txt").item(0) as HTMLElement).innerHTML = "inactive";
        }

        let welcome = header.getElementsByClassName("welcome").item(0) as HTMLElement;
        if (this.config.welcomeMessage) {
            welcome.innerHTML = this.config.welcomeMessage;
        } else {
            welcome.style.display = "none";
        }
    }

    private createIcon(type: IconType): string {
        if (type == IconType.OPEN) {
            return "<svg class=\"chat-icon-svg\" width=\"56\" height=\"56\" viewBox=\"0 0 56 56\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n" +
                "    <path class=\"svg-path-chat\" d=\"M27.9999 56C12.5359 56 -9.34526e-05 43.464 -9.27767e-05 28C-9.21007e-05 12.536 12.5359 -1.89987e-06 27.9999 -1.22392e-06C43.4639 -5.4797e-07 55.9999 12.536 55.9999 28C55.9999 36.6099 56.0001 48.5 55.9999 56C48.9999 56 34.854 56 27.9999 56Z\"/>\n" +
                "    <path d=\"M27.9999 42.25C20.1299 42.25 13.7499 35.8701 13.7499 28C13.7499 20.1299 20.1299 13.75 27.9999 13.75C35.87 13.75 42.2499 20.1299 42.2499 28L42.2499 42.25L42.2292 42.25L42.2045 42.25L42.1797 42.25L42.1548 42.25L42.1299 42.25L42.1049 42.25L42.0798 42.25L42.0546 42.25L42.0294 42.25L42.0041 42.25L41.9787 42.25L41.9532 42.25L41.9277 42.25L41.9021 42.25L41.8764 42.25L41.8507 42.25L41.8249 42.25L41.799 42.25L41.773 42.25L41.747 42.25L41.7209 42.25L41.6947 42.25L41.6685 42.25L41.6422 42.25L41.6158 42.25L41.5894 42.25L41.5629 42.25L41.5363 42.25L41.5097 42.25L41.483 42.25L41.4562 42.25L41.4294 42.25L41.4024 42.25L41.3755 42.25L41.3484 42.25L41.3213 42.25L41.2942 42.25L41.267 42.25L41.2397 42.25L41.2123 42.25L41.1849 42.25L41.1574 42.25L41.1299 42.25L41.1023 42.25L41.0746 42.25L41.0469 42.25L41.0191 42.25L40.9912 42.25L40.9633 42.25L40.9354 42.25L40.9074 42.25L40.8793 42.25L40.8511 42.25L40.8229 42.25L40.7947 42.25L40.7664 42.25L40.738 42.25L40.7095 42.25L40.6811 42.25L40.6525 42.25L40.6239 42.25L40.5953 42.25L40.5666 42.25L40.5378 42.25L40.509 42.25L40.4801 42.25L40.4512 42.25L40.4222 42.25L40.3932 42.25L40.3641 42.25L40.3349 42.25L40.3058 42.25L40.2765 42.25L40.2472 42.25L40.2179 42.25L40.1885 42.25L40.159 42.25L40.1295 42.25L40.1 42.25L40.0704 42.25L40.0408 42.25L40.0111 42.25L39.9813 42.25L39.9516 42.25L39.9217 42.25L39.8918 42.25L39.8619 42.25L39.8319 42.25L39.8019 42.25L39.7718 42.25L39.7417 42.25L39.7116 42.25L39.6814 42.25L39.6511 42.25L39.6208 42.25L39.5905 42.25L39.5601 42.25L39.5297 42.25L39.4992 42.25L39.4687 42.25L39.4382 42.25L39.4076 42.25L39.377 42.25L39.3463 42.25L39.3156 42.25L39.2848 42.25L39.254 42.25L39.2232 42.25L39.1923 42.25L39.1614 42.25L39.1305 42.25L39.0995 42.25L39.0685 42.25L39.0374 42.25L39.0063 42.25L38.9752 42.25L38.944 42.25L38.9128 42.25L38.8816 42.25L38.8503 42.25L38.819 42.25L38.7876 42.25L38.7562 42.25L38.7248 42.25L38.6934 42.25L38.6619 42.25L38.6304 42.25L38.5988 42.25L38.5672 42.25L38.5356 42.25L38.504 42.25L38.4723 42.25L38.4406 42.25L38.4088 42.25L38.3771 42.25L38.3453 42.25L38.3135 42.25L38.2816 42.25L38.2497 42.25L38.2178 42.25L38.1858 42.25L38.1539 42.25L38.1219 42.25L38.0899 42.25L38.0578 42.25L38.0257 42.25L37.9936 42.25L37.9615 42.25L37.9293 42.25L37.8972 42.25L37.8649 42.25L37.8327 42.25L37.8005 42.25L37.7682 42.25L37.7359 42.25L37.7036 42.25L37.6712 42.25L37.6388 42.25L37.6065 42.25L37.574 42.25L37.5416 42.25L37.5092 42.25L37.4767 42.25L37.4442 42.25L37.4117 42.25L37.3791 42.25L37.3466 42.25L37.314 42.25L37.2814 42.25L37.2488 42.25L37.2162 42.25L37.1835 42.25L37.1509 42.25L37.1182 42.25L37.0855 42.25L37.0528 42.25L37.0201 42.25L36.9873 42.25L36.9546 42.25L36.9218 42.25L36.889 42.25L36.8562 42.25L36.8234 42.25L36.7906 42.25L36.7577 42.25L36.7249 42.25L36.692 42.25L36.6592 42.25L36.6263 42.25L36.5934 42.25L36.5605 42.25L36.5275 42.25L36.4946 42.25L36.4617 42.25L36.4287 42.25L36.3958 42.25L36.3628 42.25L36.3298 42.25L36.2968 42.25L36.2638 42.25L36.2308 42.25L36.1978 42.25L36.1648 42.25L36.1318 42.25L36.0988 42.25L36.0657 42.25L36.0327 42.25L35.9997 42.25L35.9666 42.25L35.9336 42.25L35.9005 42.25L35.8675 42.25L35.8344 42.25L35.8013 42.25L35.7683 42.25L35.7352 42.25L35.7021 42.25L35.6691 42.25L35.636 42.25L35.6029 42.25L35.5698 42.25L35.5368 42.25L35.5037 42.25L35.4706 42.25L35.4375 42.25L35.4045 42.25L35.3714 42.25L35.3383 42.25L35.3053 42.25L35.2722 42.25L35.2391 42.25L35.2061 42.25L35.173 42.25L35.14 42.25L35.1069 42.25L35.0739 42.25L35.0409 42.25L35.0078 42.25L34.9748 42.25L34.9418 42.25L34.9088 42.25L34.8758 42.25L34.8428 42.25L34.8098 42.25L34.7768 42.25L34.7438 42.25L34.7109 42.25L34.6779 42.25L34.645 42.25L34.612 42.25L34.5791 42.25L34.5462 42.25L34.5133 42.25L34.4804 42.25L34.4475 42.25L34.4146 42.25L34.3817 42.25L34.3489 42.25L34.3161 42.25L34.2832 42.25L34.2504 42.25L34.2176 42.25L34.1848 42.25L34.1521 42.25L34.1193 42.25L34.0866 42.25L34.0539 42.25L34.0212 42.25L33.9885 42.25L33.9558 42.25L33.9231 42.25L33.8905 42.25L33.8579 42.25L33.8253 42.25L33.7927 42.25L33.7601 42.25L33.7275 42.25L33.695 42.25L33.6625 42.25L33.63 42.25L33.5975 42.25L33.5651 42.25L33.5327 42.25L33.5003 42.25L33.4679 42.25L33.4355 42.25L33.4032 42.25L33.3708 42.25L33.3385 42.25L33.3063 42.25L33.274 42.25L33.2418 42.25L33.2096 42.25L33.1774 42.25L33.1453 42.25L33.1131 42.25L33.081 42.25L33.049 42.25L33.0169 42.25L32.9849 42.25L32.9529 42.25L32.9209 42.25L32.889 42.25L32.8571 42.25L32.8252 42.25L32.7934 42.25L32.7615 42.25L32.7297 42.25L32.698 42.25L32.6662 42.25L32.6345 42.25L32.6029 42.25L32.5712 42.25L32.5396 42.25L32.508 42.25L32.4765 42.25L32.445 42.25L32.4135 42.25L32.3821 42.25L32.3507 42.25L32.3193 42.25L32.2879 42.25L32.2566 42.25L32.2254 42.25L32.1941 42.25L32.1629 42.25L32.1318 42.25L32.1006 42.25L32.0695 42.25L32.0385 42.25L32.0075 42.25L31.9765 42.25L31.9455 42.25L31.9146 42.25L31.8838 42.25L31.8529 42.25L31.8222 42.25L31.7914 42.25L31.7607 42.25L31.73 42.25L31.6994 42.25L31.6688 42.25L31.6383 42.25L31.6078 42.25L31.5774 42.25L31.5469 42.25L31.5166 42.25L31.4862 42.25L31.456 42.25L31.4257 42.25L31.3955 42.25L31.3654 42.25L31.3353 42.25L31.3052 42.25L31.2752 42.25L31.2452 42.25L31.2153 42.25L31.1854 42.25L31.1556 42.25L31.1258 42.25L31.0961 42.25L31.0664 42.25L31.0368 42.25L31.0072 42.25L30.9777 42.25L30.9482 42.25L30.9188 42.25L30.8894 42.25L30.86 42.25L30.8308 42.25L30.8015 42.25L30.7723 42.25L30.7432 42.25L30.7141 42.25L30.6851 42.25L30.6561 42.25L30.6272 42.25L30.5984 42.25L30.5696 42.25L30.5408 42.25L30.5121 42.25L30.4835 42.25L30.4549 42.25L30.4263 42.25L30.3979 42.25L30.3694 42.25L30.3411 42.25L30.3128 42.25L30.2845 42.25L30.2563 42.25L30.2282 42.25L30.2001 42.25L30.1721 42.25L30.1442 42.25L30.1163 42.25L30.0884 42.25L30.0607 42.25L30.0329 42.25L30.0053 42.25L29.9777 42.25L29.9502 42.25L29.9227 42.25L29.8953 42.25L29.8679 42.25L29.8407 42.25L29.8134 42.25L29.7863 42.25L29.7592 42.25L29.7322 42.25L29.7052 42.25L29.6783 42.25L29.6515 42.25L29.6247 42.25L29.598 42.25L29.5714 42.25L29.5449 42.25L29.5184 42.25L29.4919 42.25L29.4656 42.25L29.4393 42.25L29.4131 42.25L29.3869 42.25L29.3608 42.25L29.3348 42.25L29.3089 42.25L29.283 42.25L29.2572 42.25L29.2315 42.25L29.2058 42.25L29.1802 42.25L29.1547 42.25L29.1292 42.25L29.1039 42.25L29.0786 42.25L29.0534 42.25L29.0282 42.25L29.0031 42.25L28.9781 42.25L28.9532 42.25L28.9283 42.25L28.9036 42.25L28.8789 42.25L28.8542 42.25L28.8297 42.25L28.8052 42.25L28.7808 42.25L28.7565 42.25L28.7323 42.25L28.7081 42.25L28.684 42.25L28.66 42.25L28.6361 42.25L28.6123 42.25L28.5885 42.25L28.5648 42.25L28.5412 42.25L28.5177 42.25L28.4943 42.25L28.4709 42.25L28.4476 42.25L28.4244 42.25L28.4013 42.25L28.3783 42.25L28.3554 42.25L28.3325 42.25L28.3097 42.25L28.287 42.25L28.2644 42.25L28.2419 42.25L28.2195 42.25L28.1971 42.25L28.1749 42.25L28.1527 42.25L28.1306 42.25L28.1086 42.25L28.0867 42.25L28.0649 42.25L28.0431 42.25L28.0215 42.25L27.9999 42.25Z\" stroke=\"white\" stroke-width=\"1.5\"/>\n" +
                "    <path d=\"M23 32C24.1513 33.8233 26.1842 35.0343 28.5 35.0343C30.8158 35.0343 32.8487 33.8233 34 32\" stroke=\"white\" stroke-width=\"1.5\"/>\n" +
                "</svg>"
        } else {
            return "<svg class=\"chat-icon-svg\" width=\"56\" height=\"56\" viewBox=\"0 0 56 56\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n" +
                "<path class=\"svg-path-chat\" d=\"M56 28.0001C56 43.464 43.464 56.0001 28 56.0001C12.536 56.0001 0 43.464 0 28.0001C0 12.5361 12.536 7.17124e-05 28 7.17124e-05C36.6099 7.17124e-05 48.5 -0.000128508 56 4.54864e-05C56 7.00005 56 21.146 56 28.0001Z\"/>\n" +
                "<line x1=\"34.6893\" y1=\"21.7071\" x2=\"20.7071\" y2=\"35.6893\" stroke=\"white\" stroke-width=\"2\"/>\n" +
                "<line x1=\"35.0229\" y1=\"35.6894\" x2=\"21.0407\" y2=\"21.7072\" stroke=\"white\" stroke-width=\"2\"/>\n" +
                "</svg>\n";
        }
    }

    private renderMessages(messages) {
        //logMessage("rendering messages:" + messages);
        let currentUser = this.currentUser;
        let config = this.config;
        let messageContainer = document.getElementById("messages-thread") as HTMLElement;
        messages.forEach(function (message) {
            var className = "message";
            if (currentUser.chatql.username == message.authorId) {
                className += " thisuser";
            } else {
                className += " otheruser";
            }
            let element = document.createElement("div");
            element.className = className;
            element.innerHTML = (currentUser.chatql.username == message.authorId ? "" : "<div class='profile-image'><img src='" + config.profileImageUrl + "'></div>") +
                "<div class='content-container'><div>" + KaydohChat.replaceAll(message.content, "\n", "<BR>") + "</div><div class='date'>" + KaydohChat.dateFormat(new Date(message.createdAt)) + "</div></div>";
            messageContainer.append(element);
        });
        if (this.chatConfigStore.isChatWindowOpen) {
            messageContainer.scrollTop = messageContainer.scrollHeight;
            this.checkAndMarkAllMessagesRead();
        }
        let elements = messageContainer.getElementsByClassName("email-collection");
        if (elements && elements.length > 0) {
            let emailCollection = elements.item(0) as HTMLElement;
            messageContainer.removeChild(emailCollection);
            messageContainer.append(emailCollection);
        }
    }

    private renderChatNotification() {
        var chatNotification = document.getElementsByClassName("chat-notification").item(0) as HTMLElement;
        chatNotification.innerText = "1";//+ this.chatConfigStore.getUnreadCount();
        chatNotification.style.display = "block";
    }

    private hideChatNotification() {
        var chatNotification = document.getElementsByClassName("chat-notification").item(0) as HTMLElement;
        chatNotification.innerText = "1";// + this.chatConfigStore.getUnreadCount();
        chatNotification.style.display = "none";
    }

    private renderInitializing() {
        let messageContainer = document.getElementById("kchat-message-container") as HTMLElement;
        let element = document.createElement("div");
        element.className = "sys-message";
        element.innerHTML = "<div class='init-content'>Chat is connecting, please wait...</div><div class='date'>" + KaydohChat.dateFormat(new Date()) + "</div>";
        messageContainer.append(element);
        messageContainer.scrollTop = messageContainer.scrollHeight;
    }

    private renderChatReady() {
        let messageContainer = document.getElementById("kchat-message-container") as HTMLElement;
        if (messageContainer.getElementsByClassName("init-content").length > 0) {
            let element = document.createElement("div");
            element.className = "sys-message";
            element.innerHTML = "<div class='ready-content'>Connected!!</div><div class='date'>" + KaydohChat.dateFormat(new Date()) + "</div>";
            messageContainer.append(element);
            messageContainer.scrollTop = messageContainer.scrollHeight;
        }
    }

    private async renderEmailCollection(option) {
        if (!this.isLoggedIn) {
            this.initChat();
        }
        let elements = document.getElementsByClassName("email-collection");
        var emailCollection: HTMLElement;
        if (elements && elements.length == 0) {
            emailCollection = document.createElement("div");
            emailCollection.className = "email-collection";
            let messageContainer = document.getElementById("kchat-message-container");
            messageContainer.append(emailCollection);
            emailCollection.innerHTML = `<div class='email-message'></div>
                                        <div class='name-input'>
                                            <input type='text' placeholder='Type your name'>
                                        </div>
                                        <div class='email-input'>
                                            <input type='email' placeholder='Type your email'>
                                        </div>
                                        <div class='quick-message-input quick-message-email'>
                                            <input type='text' placeholder='Type your message'>
                                        </div>`;

            emailCollection.getElementsByClassName("email-message")
                .item(0)
                .innerHTML = "<div>" + (option ? option.others : this.config.emailSolicitation) + "</div>";

            let quickMessageEmail = emailCollection.getElementsByClassName("quick-message-email").item(0) as HTMLElement;
            quickMessageEmail.innerHTML += ("<button>Submit</button>");
            quickMessageEmail.getElementsByTagName("input").item(0).addEventListener("keypress", ev => this.onEmailSubmit(ev, option));
            quickMessageEmail.getElementsByTagName("button").item(0).addEventListener("click", ev => this.onEmailSubmit(ev, option));

            messageContainer.scrollTop = messageContainer.scrollHeight;

        } else {
            emailCollection = elements.item(0) as HTMLElement;
        }

    }

    private async renderPhoneCollection(option) {
        if (!this.isLoggedIn) {
            this.initChat();
        }
        let elements = document.getElementsByClassName("phone-collection");
        var phoneCollection: HTMLElement;
        if (elements && elements.length == 0) {
            phoneCollection = document.createElement("div");
            phoneCollection.className = "phone-collection";
            let messageContainer = document.getElementById("kchat-message-container");
            messageContainer.append(phoneCollection);
            phoneCollection.innerHTML = `<div class='phone-message'></div>
                                        <div class='name-input'>
                                            <input type='text' placeholder='Type your name'>
                                        </div>
                                        <div class='phone-input'>
                                            <input type='tel' pattern='[0-9]{3}-[0-9]{3}-[0-9]{4}' placeholder='Type your phone'>
                                        </div>
                                        <div class='quick-message-input quick-message-phone'>
                                            <input type='text' placeholder='Type your message'>
                                        </div>`;

            phoneCollection.getElementsByClassName("phone-message")
                .item(0)
                .innerHTML = "<div>" + option.others + "</div>";

            let quickMessagePhone = phoneCollection.getElementsByClassName("quick-message-phone").item(0) as HTMLElement;
            quickMessagePhone.innerHTML += ("<button>Submit</button>");
            quickMessagePhone.getElementsByTagName("input").item(0).addEventListener("keypress", ev => this.onPhoneSubmit(ev, option));
            quickMessagePhone.getElementsByTagName("button").item(0).addEventListener("click", ev => this.onPhoneSubmit(ev, option));

            messageContainer.scrollTop = messageContainer.scrollHeight;

        } else {
            phoneCollection = elements.item(0) as HTMLElement;
        }

    }

    private renderDefaultOptions(messageContainer: HTMLElement, input: HTMLInputElement) {
        let onClickOfDefaultOption = (event: Event) => {
            let ele = event.target as HTMLElement;
            let option = this.config.defaultOptions[ele.getAttribute("option-index")];
            if (option.messageType === "redirect" || option.messageType === "calendly") {
                if (window) {
                    window.open(option.others, "_blank");
                }
            } else if (option.messageType === "phone-capture") {
                this.renderPhoneCollection(option).then();
            } else if (option.messageType === "default") {
                this.renderEmailCollection(option).then();
            } else {
                input.value = option.message;
                input.focus();
                //var evt = document.createEvent("KeyboardEvent");
                //evt.initEvent("keypress", false, true);
                var evt = new KeyboardEvent('keypress', {'key': "Enter", 'code': "Enter"});
                input.dispatchEvent(evt);
                //this.onMessageInputKeypress();
            }
        };

        if (this.config.defaultOptions && this.config.defaultOptions.length > 0) {
            let defaultOptionContainer = document.createElement("div");
            defaultOptionContainer.className = "default-options";
            let theme = this.config.theme;
            this.config.defaultOptions.forEach(function (option, index) {
                let optionElement = document.createElement("div");
                optionElement.className = "option";
                // optionElement.style.borderColor = theme.bgColorCode;
                optionElement.style.borderWidth = "1px";
                optionElement.style.borderStyle = "solid";
                // optionElement.style.color = theme.bgColorCode;
                optionElement.setAttribute("option-index", "" + index);
                optionElement.innerText = option.message;
                optionElement.addEventListener("click", ev => {
                    const botProgress = document.createElement("div");
                    if (document.getElementsByClassName("bot-progress").length > 0) {
                        document.getElementsByClassName("bot-progress")[0].remove();
                    }
                    botProgress.innerHTML = "<img src=\"data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9JzMwMHB4JyB3aWR0aD0nMzAwcHgnICBmaWxsPSIjMDAwMDAwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgOTYgOTYiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDk2IDk2IiB4bWw6c3BhY2U9InByZXNlcnZlIj48Zz48Zz48cGF0aCBkPSJNMzQuMyw0OGMwLDMuMi0yLjYsNS44LTUuOCw1LjhzLTUuOC0yLjYtNS44LTUuOHMyLjYtNS44LDUuOC01LjhTMzQuMyw0NC44LDM0LjMsNDh6Ij48L3BhdGg+PHBhdGggZD0iTTUzLjgsNDhjMCwzLjItMi42LDUuOC01LjgsNS44cy01LjgtMi42LTUuOC01LjhzMi42LTUuOCw1LjgtNS44UzUzLjgsNDQuOCw1My44LDQ4eiI+PC9wYXRoPjxjaXJjbGUgY3g9IjY3LjUiIGN5PSI0OCIgcj0iNS44Ij48L2NpcmNsZT48L2c+PC9nPjwvc3ZnPg==\" style=\"\n" +
                        "    width: 63px;\n" +
                        "    top: -20px;\n" +
                        "    position: absolute;\n" +
                        "    right: 0;\n" +
                        "    animation-duration: 1.5s;\n" +
                        "    animation-name: fadeIn;\n" +
                        "\">";
                    botProgress.className = "bot-progress";
                    botProgress.style.height = "38px";
                    botProgress.style.position = "relative";
                    messageContainer.append(botProgress);
                    setTimeout(function () {
                        if (document.getElementsByClassName("bot-progress").length > 0) {
                            document.getElementsByClassName("bot-progress")[0].remove();
                        }
                        onClickOfDefaultOption(ev);
                    }, 1000);
                });
                defaultOptionContainer.append(optionElement);
            });
            messageContainer.append(defaultOptionContainer);
        }
    }

    private hideDefaultOptions() {
        let elements = document.getElementsByClassName("default-options");
        if (elements.length > 0) {
            let e = elements[0] as HTMLElement;
            e.hidden = true;
        }
    }

    private checkAndMarkAllMessagesRead() {

        const messageContainer = this.parentContainer.getElementsByClassName("message-container").item(0) as HTMLElement;
        if (messageContainer.scrollTop + messageContainer.offsetHeight == messageContainer.scrollHeight) {
            this.chatConfigStore.updateLastReadMessageTimestamp();
            this.chatConfigStore.clearUnreadCount();
            this.hideChatNotification();
        }
    }

    private static dateFormat(date: Date) {
        let m = moment(date);
        if (m.diff(Date.now(), "days") == 0) {
            return m.format("hh:mm a")
        }
        return m.format("MM/DD");
    }

    private static replaceAll = function (target, search, replacement) {
        return target.replace(new RegExp(search, 'g'), replacement);
    };

    private showDnc() {
        if (document.body.getElementsByClassName("kaydoh-chat-dnc-popup").length === 0) {
            let dncContainer = document.createElement("div");
            dncContainer.innerHTML = "<div class=\"kaydoh-chat-dnc-popup\"><div><iframe src='" + this.url + "/dnc'></iframe></div></div>"
            document.body.append(dncContainer);
            let listener = function (e) {
                if (e.data == "closeDnc") {
                    document.body.getElementsByClassName("kaydoh-chat-dnc-popup").item(0).remove();
                    window.removeEventListener("message", listener);
                }
            };
            window.addEventListener("message", listener);
        }
    }

    private async validateEmail(email) {
        var response = await request({
            method: 'GET',
            uri: this.url + "/api/chat/client/checkemail/" + email,
            json: true
        });
        console.log(response)
        console.log(response.result)
        return response.result["debounce"] && response.result["debounce"].code == 5;
    }
}

enum IconType {
    OPEN,
    CLOSE
}

class ChatConfigStore {

    private lastReadMessageTimestampKey = "lastReadMessageTimestamp";
    private unreadCount = 0;
    private lastReadMessageTimestamp = new Date(1970, 1, 1).getTime();
    private clientId;

    isChatWindowOpen = false;
    messages = [];

    constructor(clientId) {
        this.clientId = clientId;
        let lastMsgTimestamp = this.retrieve(this.lastReadMessageTimestampKey);
        this.lastReadMessageTimestamp = lastMsgTimestamp ? parseInt(lastMsgTimestamp) : new Date(1970, 1, 1).getTime()

    }

    getUnreadCount() {
        return this.unreadCount;
    }

    getLastReadMessageTimestamp() {
        return this.lastReadMessageTimestamp;
    }

    increaseUnreadCount(count) {
        this.unreadCount += count;
    }

    updateLastReadMessageTimestamp() {

        if (this.messages.length > 0) {
            this.lastReadMessageTimestamp = new Date(this.messages[this.messages.length - 1].createdAt).getTime();
            this.store(this.lastReadMessageTimestampKey, this.lastReadMessageTimestamp.toString());
        }
    }

    store(key, value) {
        window.localStorage.setItem(this.clientId + "-" + key, value);
    }

    retrieve(key) {
        return window.localStorage.getItem(this.clientId + "-" + key)
    }

    remove(key) {
        return window.localStorage.removeItem(this.clientId + "-" + key)
    }

    clearUnreadCount() {
        this.unreadCount = 0;
    }
}

export class Theme {

    name: string;
    bgColorCode: string;

}

export class ChatConfig {

    landingPageCode: String;
    landingPageUrl: String;
    welcomeMessage: string;
    emailSolicitation: string;
    profileImageUrl: string;
    profileFullName: string;
    availableOnline: boolean;
    autoPopUp: boolean = true;
    autoPopUpDelay: number = 5000;
    theme: Theme;
    defaultOptions: Array<ChatDefaultOption>;

}

export class ChatDefaultOption {
    message: string;
    messageType: string;
    others: string;
}
